import React, { Fragment, useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import 'primereact/resources/themes/lara-light-cyan/theme.css'
import { Toast } from 'primereact/toast'
import Modal from 'react-bootstrap/Modal'
import { useNavigate } from 'react-router-dom'
import { API_URL } from '../../../utils/Constants/URLS'

const FocWorkingPlatform = forwardRef(({ id, onDataUpdate, focLeadDetails: propFocLeadDetails }, ref) => {
    const navigate = useNavigate()
    const toast = useRef(null)
    const token = localStorage.getItem('token')
    const [modalMessage, setModalMessage] = useState('')
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [showDeclineModal, setShowDeclineModal] = useState(false)
    const [focLeadDetails, setFocLeadDetails] = useState(null)
    const [isCompleted, setIsCompleted] = useState(false)
    const [differentEndUser, setDifferentEndUser] = useState(null)
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phoneNumber, setPhoneNumber] = useState('')
    const [designation, setDesignation] = useState('')
    const [
        technicalAndCommercialDiscussionCompleted,
        setTechnicalAndCommercialDiscussionCompleted
    ] = useState(null)
    const [commercialProposalSubmitted, setCommercialProposalSubmitted] =
        useState(null)
    const [approvedFinanceDescription, setApprovedFinanceDescription] =
        useState('')
    const [installationVisitDone, setInstallationVisitDone] = useState(null)
    const [dateOfVisit, setDateOfVisit] = useState('')
    const [visitNotes, setVisitNotes] = useState('')
    const [
        firstRoundCommercialNegotiationStarted,
        setFirstRoundCommercialNegotiationStarted
    ] = useState(null)

    // Expose saveData method for Save All functionality
    useImperativeHandle(ref, () => ({
        saveData: async () => {
            // Skip if already completed
            if (isCompleted) {
                return { success: true, skipped: true, stage: 'workingPlatform' }
            }

            // Save current form data with pending status (no validation required for Save All)
            const workingPlatformData = {
                differentEndUser,
                contactPerson: {
                    name,
                    email,
                    phoneNumber,
                    designation
                },
                technicalAndCommercialDiscussionCompleted,
                commercialProposalSubmitted,
                approvedFinanceDescription,
                installationVisitDone,
                dateOfVisit,
                visitNotes,
                firstRoundCommercialNegotiationStarted,
                status: 'done'
            }

            try {
                const response = await fetch(`${API_URL}foc/lead/${id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        workingPlatform: workingPlatformData
                    })
                })

                const res = await response.json()
                return { success: res.error === false, stage: 'workingPlatform' }
            } catch (error) {
                console.error('Error saving working platform:', error)
                return { success: false, stage: 'workingPlatform', error }
            }
        },
        isCompleted: () => isCompleted
    }))

    // Sync with parent focLeadDetails prop
    useEffect(() => {
        if (propFocLeadDetails) {
            const leadData = propFocLeadDetails
            setFocLeadDetails(leadData)

            // Check if this stage is completed
            if (leadData?.workingPlatform?.status === 'done') {
                setIsCompleted(true)
            }

            // Prefill form logic
            const wp = leadData?.workingPlatform || leadData?.originalLeadId?.workingPlatform

            if (wp) {
                if (differentEndUser === null && wp?.differentEndUser !== undefined)
                    setDifferentEndUser(wp.differentEndUser)
                if (name === '' && wp?.contactPerson?.name) setName(wp.contactPerson.name)
                if (email === '' && wp?.contactPerson?.email) setEmail(wp.contactPerson.email)
                if (phoneNumber === '' && wp?.contactPerson?.phoneNumber)
                    setPhoneNumber(wp.contactPerson.phoneNumber)
                if (designation === '' && wp?.contactPerson?.designation)
                    setDesignation(wp.contactPerson.designation)

                if (technicalAndCommercialDiscussionCompleted === null && wp?.technicalAndCommercialDiscussionCompleted !== undefined)
                    setTechnicalAndCommercialDiscussionCompleted(
                        wp?.technicalAndCommercialDiscussionCompleted
                    )

                if (commercialProposalSubmitted === null && wp?.commercialProposalSubmitted !== undefined)
                    setCommercialProposalSubmitted(wp?.commercialProposalSubmitted)

                if (firstRoundCommercialNegotiationStarted === null && wp?.firstRoundCommercialNegotiationStarted !== undefined)
                    setFirstRoundCommercialNegotiationStarted(
                        wp?.firstRoundCommercialNegotiationStarted
                    )

                if (approvedFinanceDescription === '' && wp?.approvedFinanceDescription)
                    setApprovedFinanceDescription(wp?.approvedFinanceDescription)

                if (installationVisitDone === null && wp?.installationVisitDone !== undefined)
                    setInstallationVisitDone(wp?.installationVisitDone)

                if (dateOfVisit === '' && wp?.dateOfVisit)
                    setDateOfVisit(new Date(wp.dateOfVisit).toISOString().split('T')[0])

                if (visitNotes === '' && wp?.visitNotes)
                    setVisitNotes(wp.visitNotes)
            }
        }
    }, [propFocLeadDetails])

    // Fetch FOC Lead Details
    useEffect(() => {
        const fetchFocLead = async () => {
            try {
                const res = await fetch(`${API_URL}foc/lead/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                const response = await res.json()
                const leadData = response.data || response
                setFocLeadDetails(leadData)

                // Check if this stage is completed
                if (leadData?.workingPlatform?.status === 'done') {
                    setIsCompleted(true)
                }

                // Prefill form: use FOC lead data if exists, otherwise use original lead data
                // Priority: FOC lead's workingPlatform > originalLeadId's workingPlatform
                const wp = leadData?.workingPlatform || leadData?.originalLeadId?.workingPlatform

                if (wp && !propFocLeadDetails) {
                    if (wp?.differentEndUser !== undefined)
                        setDifferentEndUser(wp.differentEndUser)
                    if (wp?.contactPerson?.name) setName(wp.contactPerson.name)
                    if (wp?.contactPerson?.email) setEmail(wp.contactPerson.email)
                    if (wp?.contactPerson?.phoneNumber)
                        setPhoneNumber(wp.contactPerson.phoneNumber)
                    if (wp?.contactPerson?.designation)
                        setDesignation(wp.contactPerson.designation)

                    if (wp?.technicalAndCommercialDiscussionCompleted !== undefined)
                        setTechnicalAndCommercialDiscussionCompleted(
                            wp?.technicalAndCommercialDiscussionCompleted
                        )

                    if (wp?.commercialProposalSubmitted !== undefined)
                        setCommercialProposalSubmitted(wp?.commercialProposalSubmitted)

                    if (wp?.firstRoundCommercialNegotiationStarted !== undefined)
                        setFirstRoundCommercialNegotiationStarted(
                            wp?.firstRoundCommercialNegotiationStarted
                        )

                    if (wp?.approvedFinanceDescription)
                        setApprovedFinanceDescription(wp?.approvedFinanceDescription)

                    if (wp?.installationVisitDone !== undefined)
                        setInstallationVisitDone(wp?.installationVisitDone)

                    if (wp?.dateOfVisit)
                        setDateOfVisit(new Date(wp?.dateOfVisit).toISOString().split('T')[0])

                    if (wp?.visitNotes) setVisitNotes(wp?.visitNotes)
                }
            } catch (e) {
                console.error('Error fetching FOC lead:', e)
            }
        }

        if (id) {
            fetchFocLead()
        }
    }, [id, token])

    const handleCloseModal = () => {
        setShowSuccessModal(false)
        setShowDeclineModal(false)
        window.location.reload()
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        // Check if at least one line item exists
        const lineItems = focLeadDetails?.productRequirements?.crossSells || []
        const activeLineItems = lineItems.filter(item => item.status !== 'deleted')

        if (activeLineItems.length === 0) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please add at least one line item before submitting Working Platform',
                life: 4000
            })
            return
        }

        if (differentEndUser === null) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select End User different from customer or Not',
                life: 3000
            })
        } else if (differentEndUser === true && name === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter End User Name',
                life: 3000
            })
        } else if (differentEndUser === true && phoneNumber === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter End User Phone',
                life: 3000
            })
        } else if (differentEndUser === true && designation === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter End User Designation',
                life: 3000
            })
        } else if (differentEndUser === true && email === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter End User Email',
                life: 3000
            })
        } else if (technicalAndCommercialDiscussionCompleted === null) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail:
                    'Please Select Technical and Commercial discussion completed or Not',
                life: 3000
            })
        } else if (technicalAndCommercialDiscussionCompleted === false) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail:
                    'Lead Cannot be proceed since Technical and Commercial discussion is not completed',
                life: 3000
            })
        } else if (commercialProposalSubmitted === null) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Commercial Proposal Submitted or Not',
                life: 3000
            })
        } else if (commercialProposalSubmitted === false) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail:
                    'Lead cannot be proceed since commercial proposal is not submitted',
                life: 3000
            })
        } else if (firstRoundCommercialNegotiationStarted === null) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail:
                    'Please Select First Round of Commercial Negotiation Started or Not',
                life: 3000
            })
        } else if (firstRoundCommercialNegotiationStarted === false) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail:
                    'Lead Cannot be proceed since first round of commercial negotiation is not started',
                life: 3000
            })
        } else if (approvedFinanceDescription === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter Approved Finance Description',
                life: 3000
            })
        } else if (installationVisitDone === null) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Installation Visit Done or Not',
                life: 3000
            })
        } else if (installationVisitDone === true && dateOfVisit === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter the Date of Visit',
                life: 3000
            })
        } else if (installationVisitDone === true && visitNotes === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter the Visit Note',
                life: 3000
            })
        } else {
            try {
                const response = await fetch(`${API_URL}foc/lead/${id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        workingPlatform: {
                            differentEndUser,
                            contactPerson: {
                                name,
                                email,
                                phoneNumber,
                                designation
                            },
                            technicalAndCommercialDiscussionCompleted,
                            commercialProposalSubmitted,
                            approvedFinanceDescription,
                            installationVisitDone,
                            dateOfVisit,
                            visitNotes,
                            firstRoundCommercialNegotiationStarted,
                            status: 'done'
                        }
                    })
                })

                const res = await response.json()
                if (res.error === false) {
                    setModalMessage('Working Platform Updated Successfully')
                    setShowSuccessModal(true)
                } else {
                    setModalMessage('Something Went Wrong')
                    setShowDeclineModal(true)
                }
            } catch (error) {
                setModalMessage('Oops! Error Occurred While Adding Data')
                setShowDeclineModal(true)
            }
        }
    }

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return '-'
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            })
        } catch {
            return dateString
        }
    }

    return (
        <Fragment>
            <div>
                {isCompleted ? (
                    // Completed View - Read Only Display
                    <div className='completed-stage-view'>
                        <div className='row g-3'>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Is End User different from customer</label>
                                    <p className='mb-0 fs-6'>{differentEndUser === true ? 'Yes' : differentEndUser === false ? 'No' : '-'}</p>
                                </div>
                            </div>
                            {differentEndUser === true && (
                                <>
                                    <div className='col-md-12'>
                                        <h6 className='text-muted'>End User Details</h6>
                                    </div>
                                    <div className='col-md-3'>
                                        <div className='mb-3'>
                                            <label className='form-label text-muted fw-semibold'>Name</label>
                                            <p className='mb-0 fs-6'>{name || '-'}</p>
                                        </div>
                                    </div>
                                    <div className='col-md-3'>
                                        <div className='mb-3'>
                                            <label className='form-label text-muted fw-semibold'>Mobile Number</label>
                                            <p className='mb-0 fs-6'>{phoneNumber || '-'}</p>
                                        </div>
                                    </div>
                                    <div className='col-md-3'>
                                        <div className='mb-3'>
                                            <label className='form-label text-muted fw-semibold'>Designation</label>
                                            <p className='mb-0 fs-6'>{designation || '-'}</p>
                                        </div>
                                    </div>
                                    <div className='col-md-3'>
                                        <div className='mb-3'>
                                            <label className='form-label text-muted fw-semibold'>Email</label>
                                            <p className='mb-0 fs-6'>{email || '-'}</p>
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Technical and Commercial discussion completed</label>
                                    <p className='mb-0 fs-6'>{technicalAndCommercialDiscussionCompleted === true ? 'Yes' : technicalAndCommercialDiscussionCompleted === false ? 'No' : '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Commercial Proposal Submitted</label>
                                    <p className='mb-0 fs-6'>{commercialProposalSubmitted === true ? 'Yes' : commercialProposalSubmitted === false ? 'No' : '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>First Round of Commercial negotiation started?</label>
                                    <p className='mb-0 fs-6'>{firstRoundCommercialNegotiationStarted === true ? 'Yes' : firstRoundCommercialNegotiationStarted === false ? 'No' : '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Approved Finance Description</label>
                                    <p className='mb-0 fs-6'>{approvedFinanceDescription || '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Site Visit Done?</label>
                                    <p className='mb-0 fs-6'>{installationVisitDone === true ? 'Yes' : installationVisitDone === false ? 'No' : '-'}</p>
                                </div>
                            </div>
                            {installationVisitDone === true && (
                                <>
                                    <div className='col-md-6'>
                                        <div className='mb-3'>
                                            <label className='form-label text-muted fw-semibold'>Date of Visit</label>
                                            <p className='mb-0 fs-6'>{formatDate(dateOfVisit)}</p>
                                        </div>
                                    </div>
                                    <div className='col-md-12'>
                                        <div className='mb-3'>
                                            <label className='form-label text-muted fw-semibold'>Visit Notes</label>
                                            <p className='mb-0 fs-6'>{visitNotes || '-'}</p>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className='d-flex align-items-center mt-2'>
                            <span className='badge bg-success'>
                                <i className='bi bi-check-circle me-1'></i> Completed
                            </span>
                        </div>
                    </div>
                ) : (
                    // Form View - Editable
                    <form className='row g-3 needs-validation' noValidate>
                        <div className='row mt-3'>
                            <div className='col-md-6'>
                                <label htmlFor='name' className='form-label'>
                                    Is End User different from customer{' '}
                                    <span className='text-danger'>*</span>
                                </label>
                                <br />
                                <div className='form-check form-check-inline'>
                                    <input
                                        required
                                        name='differentEndUser'
                                        value='true'
                                        checked={differentEndUser === true}
                                        onChange={() => setDifferentEndUser(true)}
                                        className='form-check-input'
                                        type='radio'
                                        id='differentEndUserYes'
                                    />
                                    <label
                                        className='form-check-label'
                                        htmlFor='differentEndUserYes'
                                    >
                                        Yes
                                    </label>
                                </div>
                                <div className='form-check form-check-inline'>
                                    <input
                                        required
                                        name='differentEndUser'
                                        value='false'
                                        checked={differentEndUser === false}
                                        onChange={() => setDifferentEndUser(false)}
                                        className='form-check-input'
                                        type='radio'
                                        id='differentEndUserNo'
                                    />
                                    <label
                                        className='form-check-label'
                                        htmlFor='differentEndUserNo'
                                    >
                                        No
                                    </label>
                                </div>
                            </div>
                        </div>

                        {differentEndUser === true && (
                            <div className='row mt-3'>
                                <h5>End User Details</h5>
                                <div className='col-md-6'>
                                    <label htmlFor='name' className='form-label'>
                                        Name <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        name='name'
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        type='text'
                                        className='form-control'
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <label htmlFor='phoneNumber' className='form-label'>
                                        Mobile Number <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        name='phoneNumber'
                                        value={phoneNumber}
                                        onChange={e => setPhoneNumber(e.target.value)}
                                        type='tel'
                                        className='form-control'
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <label htmlFor='designation' className='form-label'>
                                        Designation <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        name='designation'
                                        value={designation}
                                        onChange={e => setDesignation(e.target.value)}
                                        type='text'
                                        className='form-control'
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <label htmlFor='email' className='form-label'>
                                        Email <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        name='email'
                                        type='email'
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className='form-control'
                                    />
                                </div>
                            </div>
                        )}

                        <div className='row mt-3'>
                            <div className='col-md-7'>
                                <label htmlFor='discussion' className='form-label'>
                                    Technical and Commercial discussion completed{' '}
                                    <span className='text-danger'>*</span>
                                </label>
                                <br />
                                <div className='form-check form-check-inline'>
                                    <input
                                        required
                                        value='true'
                                        checked={technicalAndCommercialDiscussionCompleted === true}
                                        onChange={() =>
                                            setTechnicalAndCommercialDiscussionCompleted(true)
                                        }
                                        className='form-check-input'
                                        type='radio'
                                        name='technicalAndCommercialDiscussionCompleted'
                                        id='discussionYes'
                                    />
                                    <label className='form-check-label' htmlFor='discussionYes'>
                                        Yes
                                    </label>
                                </div>
                                <div className='form-check form-check-inline'>
                                    <input
                                        required
                                        value='false'
                                        checked={technicalAndCommercialDiscussionCompleted === false}
                                        onChange={() =>
                                            setTechnicalAndCommercialDiscussionCompleted(false)
                                        }
                                        className='form-check-input'
                                        type='radio'
                                        name='technicalAndCommercialDiscussionCompleted'
                                        id='discussionNo'
                                    />
                                    <label className='form-check-label' htmlFor='discussionNo'>
                                        No
                                    </label>
                                </div>
                            </div>

                            <div className='col-md-5'>
                                <label htmlFor='com-proposal' className='form-label'>
                                    Commercial Proposal Submitted{' '}
                                    <span className='text-danger'>*</span>
                                </label>
                                <br />
                                <div className='form-check form-check-inline'>
                                    <input
                                        required
                                        value='true'
                                        checked={commercialProposalSubmitted === true}
                                        onChange={() => setCommercialProposalSubmitted(true)}
                                        name='commercialProposalSubmitted'
                                        className='form-check-input'
                                        type='radio'
                                        id='com-proposalYes'
                                    />
                                    <label className='form-check-label' htmlFor='com-proposalYes'>
                                        Yes
                                    </label>
                                </div>
                                <div className='form-check form-check-inline'>
                                    <input
                                        required
                                        value='false'
                                        checked={commercialProposalSubmitted === false}
                                        onChange={() => setCommercialProposalSubmitted(false)}
                                        className='form-check-input'
                                        type='radio'
                                        name='commercialProposalSubmitted'
                                        id='com-proposalNo'
                                    />
                                    <label className='form-check-label' htmlFor='com-proposalNo'>
                                        No
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className='row mt-3'>
                            <div className='col-md-7'>
                                <label htmlFor='com-negotiation' className='form-label'>
                                    First Round of Commercial negotiation started?{' '}
                                    <span className='text-danger'>*</span>
                                </label>
                                <br />
                                <div className='form-check form-check-inline'>
                                    <input
                                        required
                                        value='true'
                                        checked={firstRoundCommercialNegotiationStarted === true}
                                        onChange={() =>
                                            setFirstRoundCommercialNegotiationStarted(true)
                                        }
                                        className='form-check-input'
                                        type='radio'
                                        name='firstRoundCommercialNegotiationStarted'
                                        id='com-negotiationYes'
                                    />
                                    <label
                                        className='form-check-label'
                                        htmlFor='com-negotiationYes'
                                    >
                                        Yes
                                    </label>
                                </div>
                                <div className='form-check form-check-inline'>
                                    <input
                                        required
                                        value='false'
                                        checked={firstRoundCommercialNegotiationStarted === false}
                                        onChange={() =>
                                            setFirstRoundCommercialNegotiationStarted(false)
                                        }
                                        className='form-check-input'
                                        type='radio'
                                        name='firstRoundCommercialNegotiationStarted'
                                        id='com-negotiationNo'
                                    />
                                    <label className='form-check-label' htmlFor='com-negotiationNo'>
                                        No
                                    </label>
                                </div>
                            </div>

                            <div className='col-md-5'>
                                <label
                                    htmlFor='approvedFinanceDescription'
                                    className='form-label'
                                >
                                    Approved Finance Description{' '}
                                    <span className='text-danger'>*</span>
                                </label>
                                <select
                                    name='approvedFinanceDescription'
                                    value={approvedFinanceDescription}
                                    onChange={e => setApprovedFinanceDescription(e.target.value)}
                                    type='text'
                                    className='form-select'
                                    id='approvedFinanceDescription'
                                >
                                    <option value=''>Select</option>
                                    <option value='Bank'>Bank</option>
                                    <option value='Financial Institute'>Financial Institute</option>
                                    <option value='Own Firm'>Own Firm</option>
                                    <option value='OPEX Budget'>OPEX Budget</option>
                                    <option value='Others'>Others</option>
                                </select>
                            </div>
                        </div>

                        <div className='row mt-3'>
                            <div className='col-md-6'>
                                <label htmlFor='installation-visit-done' className='form-label'>
                                    Site Visit Done? <span className='text-danger'>*</span>
                                </label>
                                <br />
                                <div className='form-check form-check-inline'>
                                    <input
                                        required
                                        value='true'
                                        checked={installationVisitDone === true}
                                        onChange={() => setInstallationVisitDone(true)}
                                        className='form-check-input'
                                        type='radio'
                                        name='installationVisitDone'
                                        id='installation-visit-doneYes'
                                    />
                                    <label
                                        className='form-check-label'
                                        htmlFor='installation-visit-doneYes'
                                    >
                                        Yes
                                    </label>
                                </div>
                                <div className='form-check form-check-inline'>
                                    <input
                                        required
                                        value='false'
                                        checked={installationVisitDone === false}
                                        onChange={() => setInstallationVisitDone(false)}
                                        className='form-check-input'
                                        type='radio'
                                        name='installationVisitDone'
                                        id='installation-visit-doneNo'
                                    />
                                    <label
                                        className='form-check-label'
                                        htmlFor='installation-visit-doneNo'
                                    >
                                        No
                                    </label>
                                </div>
                            </div>

                            {installationVisitDone === true && (
                                <>
                                    <div className='col-md-6'>
                                        <label htmlFor='dateOfVisit' className='form-label'>
                                            Date and time of Visit{' '}
                                            <span className='text-danger'>*</span>
                                        </label>
                                        <input
                                            value={dateOfVisit}
                                            onClick={e => e.target.showPicker && e.target.showPicker()}
                                            onChange={e => setDateOfVisit(e.target.value)}
                                            className='form-control'
                                            type='date'
                                            name='dateOfVisit'
                                            id='dateOfVisit'
                                        />
                                    </div>
                                    <div className='col-md-6'>
                                        <label htmlFor='enquiry-details' className='form-label'>
                                            Visit Note <span className='text-danger'>*</span>
                                        </label>
                                        <textarea
                                            name='visitNote'
                                            value={visitNotes}
                                            onChange={e => setVisitNotes(e.target.value)}
                                            className='form-control'
                                            id='enquiry-details'
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <div className='col-md-12 d-flex justify-content-end gap-2'>
                            <button
                                onClick={handleSubmit}
                                className='btn add-btn text-white'
                                type='submit'
                            >
                                Save
                            </button>
                        </div>
                    </form>
                )}
                <Modal show={showSuccessModal} centered>
                    <Modal.Body>
                        <div className='my-8 text-center'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='w-14 shrink-0 fill-green-500 inline'
                                viewBox='0 0 512 512'
                            >
                                <path
                                    d='M383.841 171.838c-7.881-8.31-21.02-8.676-29.343-.775L221.987 296.732l-63.204-64.893c-8.005-8.213-21.13-8.393-29.35-.387-8.213 7.998-8.386 21.137-.388 29.35l77.492 79.561a20.687 20.687 0 0 0 14.869 6.275 20.744 20.744 0 0 0 14.288-5.694l147.373-139.762c8.316-7.888 8.668-21.027.774-29.344z'
                                    data-original='#000000'
                                />
                                <path
                                    d='M256 0C114.84 0 0 114.84 0 256s114.84 256 256 256 256-114.84 256-256S397.16 0 256 0zm0 470.487c-118.265 0-214.487-96.214-214.487-214.487 0-118.265 96.221-214.487 214.487-214.487 118.272 0 214.487 96.221 214.487 214.487 0 118.272-96.215 214.487-214.487 214.487z'
                                    data-original='#000000'
                                />
                            </svg>
                            <h4 className='text-xl text-gray-800 font-semibold mt-4'>
                                Good Job
                            </h4>
                            <p className='text-sm text-gray-500 leading-relaxed mt-4'>
                                {modalMessage}!
                            </p>
                        </div>

                        <button
                            type='button'
                            onClick={handleCloseModal}
                            className='px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none bg-green-500 hover:bg-green-700'
                        >
                            Okay
                        </button>
                    </Modal.Body>
                </Modal>
                <Modal show={showDeclineModal} centered>
                    <Modal.Body>
                        <div className='my-8 text-center'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='w-14 fill-red-500 inline'
                                viewBox='0 0 286.054 286.054'
                            >
                                <path
                                    fill='#e2574c'
                                    d='M143.027 0C64.04 0 0 64.04 0 143.027c0 78.996 64.04 143.027 143.027 143.027 78.996 0 143.027-64.022 143.027-143.027C286.054 64.04 222.022 0 143.027 0zm0 259.236c-64.183 0-116.209-52.026-116.209-116.209S78.844 26.818 143.027 26.818s116.209 52.026 116.209 116.209-52.026 116.209-116.209 116.209zm.009-196.51c-10.244 0-17.995 5.346-17.995 13.981v79.201c0 8.644 7.75 13.972 17.995 13.972 9.994 0 17.995-5.551 17.995-13.972V76.707c-.001-8.43-8.001-13.981-17.995-13.981zm0 124.997c-9.842 0-17.852 8.01-17.852 17.86 0 9.833 8.01 17.843 17.852 17.843s17.843-8.01 17.843-17.843c-.001-9.851-8.001-17.86-17.843-17.86z'
                                    data-original='#e2574c'
                                ></path>
                            </svg>
                            <h4 className='text-xl text-gray-800 font-semibold mt-4'>
                                Error Occurred
                            </h4>
                            <p className='text-sm text-gray-500 leading-relaxed mt-4'>
                                {modalMessage}!
                            </p>
                        </div>

                        <button
                            type='button'
                            onClick={handleCloseModal}
                            className='px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none bg-red-500 hover:bg-red-700'
                        >
                            Okay
                        </button>
                    </Modal.Body>
                </Modal>
                <Toast ref={toast} />
            </div>
        </Fragment>
    )
})

export default FocWorkingPlatform
